import { useMemo } from "react";

import { useQuery } from "react-query";

import { AxiosInstance } from "api";
import { useGetCareEventsSuggested } from "api/hooks/useGetCareEventsSuggested";
import { useGetPatients } from "api/hooks/useGetPatients";
import { useProfileContext } from "contexts/ProfileContext";
import { QUERY_KEYS } from "types/QueryKeys";

const fetchEventCards = async () => AxiosInstance.get(`dashboard/actions`);

interface EventCardsQuery {
  data?: { [key: string]: number };
  error: unknown;
  isLoading: boolean;
  refetch: () => void;
}

export const useEventCards = (): EventCardsQuery => {
  const { profile } = useProfileContext();
  const { data, error, isLoading, refetch } = useQuery(QUERY_KEYS.dashboard.eventCards, fetchEventCards);

  const { data: patients } = useGetPatients({ userID: profile?.id ?? 0 }, { enabled: Boolean(profile?.id) });
  const patientIDs = useMemo(() => patients?.map(p => p.id) ?? [], [patients]);
  const { data: suggestedCareEvents = [] } = useGetCareEventsSuggested(patientIDs, {
    enabled: patientIDs.length > 0,
  });

  const mergedData = useMemo(() => {
    if (!data?.data) return undefined;

    return {
      ...data.data,
      // Merge the suggested care events count with the unsigned care events count from the main query
      unsigned_care_events: (data.data.unsigned_care_events ?? 0) + suggestedCareEvents.length,
    };
  }, [data?.data, suggestedCareEvents.length]);

  return { data: mergedData, error, isLoading, refetch };
};
